import { useState } from "react";
import { ArrowLeft, Cylinder, Mail, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ForgotPassword({ onBack }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] bg-grid flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="h-12 w-12 rounded-lg bg-amber-500/10 border border-amber-500/40 flex items-center justify-center">
            <Cylinder className="h-7 w-7 text-amber-400" />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight font-mono text-slate-100">TANK VOLUME CALCULATOR</h1>
        </div>

        <div className="bg-[#131924] border border-[#243046] rounded-xl p-8 shadow-2xl rise">
          {sent ? (
            <div className="space-y-4 text-center" data-testid="forgot-sent-message">
              <div className="mx-auto h-12 w-12 rounded-full bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center">
                <Send className="h-6 w-6 text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold font-mono text-slate-100">Check your inbox</h2>
              <p className="text-sm text-slate-400 font-mono">If an account exists for <span className="text-slate-200">{email}</span>, a password reset link has been sent. It expires in 1 hour.</p>
              <Button data-testid="back-to-login-btn" onClick={onBack} variant="outline" className="border-[#243046] bg-transparent text-slate-200 hover:bg-[#1A2232]">
                <ArrowLeft />Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
              <div>
                <h2 className="text-lg font-semibold font-mono text-slate-100">Forgot password</h2>
                <p className="text-xs text-slate-500 font-mono mt-1">Enter your email and we&rsquo;ll send you a reset link.</p>
              </div>
              <div>
                <p className="text-[11px] font-mono text-slate-500 mb-1">Email</p>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input data-testid="forgot-email-input" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="pl-9 bg-[#0D131E] border-[#243046] font-mono text-slate-100 focus-visible:ring-amber-500" />
                </div>
              </div>
              {error && <p data-testid="forgot-error" className="text-sm text-red-400 font-mono bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>}
              <Button data-testid="forgot-submit-btn" type="submit" disabled={busy} className="w-full bg-amber-500 text-[#0B0F17] hover:bg-amber-400 font-semibold">
                <Send />{busy ? "Sending…" : "Send reset link"}
              </Button>
              <button type="button" data-testid="forgot-back-btn" onClick={onBack} className="w-full text-xs font-mono text-slate-500 hover:text-slate-300 flex items-center justify-center gap-1">
                <ArrowLeft className="h-3 w-3" />Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
